/**
 * «منشی هوشمند» in the doctor panel — the shell's side of the secretary (PM-0517).
 *
 * `SecretaryProvider` wraps the desktop shell (WebLayout) once, so the record button
 * at the foot of the sidebar (SecretaryRecorder) and what it brings back belong to
 * no screen and survive moving between them.
 *
 * `SecretaryHost` is mounted once beside them and owns:
 *   • the keys — F1 records and stops from any screen, Esc throws a recording away,
 *     Shift+F1 opens the newest result. This app has no shortcut registry, so they
 *     are one window listener here, and F1's browser help is held back;
 *   • the notifications (SecretaryNotices), and where opening one goes
 *     (model.routeOf): «ثبت نوبت» filled in, the record for «باز کردن پرونده», and
 *     the result's own window (SecretaryWindow) when something is to be read or chosen;
 *   • the hand-off to «ثبت نوبت» — the existing booking form, opened filled in. The
 *     doctor's press books it; پذیرش re-reads on the scheduling event it raises.
 */
import React from 'react';
import { Alert, useToast } from '@parsina/framework';

import BookAppointmentDialog from '../components/BookAppointmentDialog';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { bookingHandoff, routeOf } from './model';
import SecretaryNotices from './SecretaryNotices';
import SecretaryWindow from './SecretaryWindow';
import { useSecretaryController } from './useSecretaryController';
import { useSecretaryStrings } from './secretaryStrings';

const SecretaryContext = React.createContext(null);

/** The record button's state and actions — null outside a SecretaryProvider. */
export function useSecretaryRecorder() {
  return React.useContext(SecretaryContext);
}

export function SecretaryProvider({ children }) {
  const controller = useSecretaryController();
  return <SecretaryContext.Provider value={controller}>{children}</SecretaryContext.Provider>;
}

/** Leave the screen the way the sidebar does: through the visit's unsaved-work guard. */
function guarded(go) {
  if (typeof window !== 'undefined' && window.__parsina_visit_guard) window.__parsina_visit_guard(go);
  else go();
}

/** The line above the filled-in form: where its values came from. */
function FormNotice({ booking, s }) {
  const parts = [s('secretary.form.notice')];
  if (booking.clinicAssumed) parts.push(s(`secretary.form.${booking.clinicAssumed}`));
  if (booking.newPatientName) parts.push(s('secretary.form.newPatient'));
  if (booking.newPatientPhone) parts.push(s('secretary.form.heardPhone', { phone: booking.newPatientPhone }));
  return <Alert variant="info" data-testid="secretary-form-notice">{parts.join(' ')}</Alert>;
}

/**
 * «ثبت نوبت», filled in. Its own component so the toast is asked for only while a
 * booking is open — the shell renders without one in places that have no toasts.
 */
function BookingHandoff({ booking, onClose, s }) {
  const toast = useToast();
  return (
    <BookAppointmentDialog
      open
      isMobile={false}
      preset={booking}
      notice={<FormNotice booking={booking} s={s} />}
      onClose={onClose}
      onBooked={(result) => {
        toast.success(s(result?.outcome === 'requested' ? 'secretary.form.requested' : 'secretary.form.booked'));
      }}
    />
  );
}

export default function SecretaryHost({ sidebarWidth = 0 }) {
  const c = useSecretaryRecorder();
  const s = useSecretaryStrings();
  const navigate = useAppNavigate();
  const [booking, setBooking] = React.useState(null);
  const busy = Boolean(booking || c?.openItem);

  const openPatient = React.useCallback((id) => guarded(() => navigate('patient-detail', { patientId: id })), [navigate]);

  /** Where a result goes when it is opened. */
  const openResult = React.useCallback((item) => {
    const route = item.error ? 'window' : routeOf(item.result);
    if (route === 'form' || route === 'formDay') {
      c.dismiss(item.id);
      setBooking({ ...bookingHandoff(item.result.booking, route === 'form' ? 0 : -1), key: item.id });
      return;
    }
    if (route === 'navigate') {
      c.dismiss(item.id);
      openPatient(item.result.patient.id);
      return;
    }
    c.open(item.id);
  }, [c, openPatient]);

  // Kept in a ref so the one listener always sees the current state.
  const keys = React.useRef(null);
  keys.current = { c, busy, openResult };
  React.useEffect(() => {
    const onKey = (e) => {
      const { c: ctl, busy: blocked, openResult: open } = keys.current;
      if (!ctl) return;
      if (e.key === 'F1' && e.shiftKey) {
        if (blocked || !ctl.items.length) return;
        e.preventDefault();
        open(ctl.items[ctl.items.length - 1]);
        return;
      }
      if (e.key === 'F1') {
        e.preventDefault(); // the browser's help page is not what F1 means here
        if (!blocked) ctl.toggle();
        return;
      }
      if (e.key === 'Escape' && ctl.recording && !blocked) {
        e.preventDefault();
        ctl.discard();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!c) return null;
  const item = c.openItem;
  return (
    <>
      <SecretaryNotices items={c.items} onOpen={openResult} onDismiss={c.dismiss} sidebarWidth={sidebarWidth} />
      {item ? (
        <SecretaryWindow
          key={item.id}
          item={item}
          onClose={c.close}
          onBook={(handoff) => { c.dismiss(item.id); setBooking({ ...handoff, key: item.id }); }}
          onAnswer={(choice) => c.answer(item.id, choice)}
          onRecordAgain={() => { c.dismiss(item.id); c.start(); }}
          onReception={() => { c.dismiss(item.id); guarded(() => navigate('reception')); }}
          onTopUp={() => { c.dismiss(item.id); guarded(() => navigate('credits')); }}
        />
      ) : null}
      {booking ? <BookingHandoff key={booking.key} booking={booking} onClose={() => setBooking(null)} s={s} /> : null}
    </>
  );
}
